import { Close, DownloadOutlined } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Loader from 'components/v2/common/loader';
import PDFViewer from 'components/v2/common/pdfViewer';

import useTranslate from 'hooks/useTranslate';
import { downloadBlob } from 'utils/file';

interface Props {
    title: string;
    subtitle?: string;
    file: Blob | string | null;
    loading?: boolean;
    downloadFileName?: string;
    onClose: () => void;
}

/** Read-only PDF dialog for an already-fetched document (Blob or data URL). */
const PdfPreviewDialog = ({ title, subtitle, file, loading, downloadFileName, onClose }: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleDownload = () => {
        if (!file) return;
        if (file instanceof Blob) {
            downloadBlob(file, downloadFileName || 'document.pdf');
        } else {
            window.open(file, '_blank', 'noopener');
        }
    };

    return (
        <Dialog
            open
            onClose={loading ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={fullScreen}
            slotProps={{
                paper: {
                    sx: {
                        height: fullScreen ? '100%' : '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <DialogTitle
                component="div"
                sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {title}
                        </Typography>
                        {subtitle ? (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Tooltip title={translate('download')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleDownload}
                                    disabled={!file || loading}
                                    aria-label={translate('download')}
                                >
                                    <DownloadOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={onClose}
                            disabled={loading}
                            aria-label={translate('close')}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: 'grey.50',
                }}
            >
                {loading || !file ? (
                    <Loader rootSx={{ minHeight: 300 }} message={translate('loading')} />
                ) : (
                    <PDFViewer file={file} />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PdfPreviewDialog;
